<!--
  WidgetsPanel.vue — the per-tracker DETAILS panel, ATTACHED to the right of the Objects
  list (matches go.lacak.io: the details panel sits beside the object list in the left
  region, not floating on the far side of the map). Structure mirrors the source:
    · Street View header (Google Street View Static) + tracker name + close
    · "Widgets" bar (collapse · reorder · settings)
    · a single compact indicator row  (status · battery · signal)
    · compact widget ROWS (icon + value + relative timestamp), draggable to reorder —
      position, odometer, driver, sensors, inputs, outputs (tracker/output/set), events
  Every value is live from the states stream. Reconstructed from the live details panel +
  RECOVERY Pass-8 analysis/online-widgets/ONLINE-WIDGETS.md.
-->
<template>
  <div class="widgets-panel" :class="{ shifted: objectsVisible }">
    <!-- Street View header — click to open interactive Google Street View -->
    <div class="wp-street" @click="openStreetView" v-tip="'Open Street View'">
      <img v-if="streetUrl && !streetErr" :src="streetUrl" alt="" @error="streetErr = true" />
      <div v-else class="wp-street-fallback"></div>
      <div class="wp-street-overlay"><i class="material-icons">streetview</i> Street view</div>
      <div class="wp-street-title" @click.stop="pickerOpen = !pickerOpen">
        <span>{{ tracker.label || ('#' + tracker.id) }}</span>
        <i class="material-icons">arrow_drop_down</i>
      </div>
      <button class="wp-x" @click.stop="$emit('close')"><i class="material-icons">close</i></button>
      <!-- tracker-select dropdown: switch the shown object without leaving the panel -->
      <div v-if="pickerOpen" class="wp-picker" @click.stop>
        <div v-for="t in trackers" :key="t.id" class="wp-picker-item" :class="{ sel: t.id === tracker.id }" @click="selectTracker(t)">
          <i class="wp-picker-dot" :class="pickStatus(t)"></i>{{ t.label || ('#' + t.id) }}
        </div>
      </div>
    </div>

    <!-- Widgets bar -->
    <div class="wp-bar">
      <span class="wp-bar-title">Widgets</span>
      <button class="wp-bar-btn" v-tip="detailed ? 'Compact view' : 'Detailed view'" @click="detailed = !detailed"><i class="material-icons">{{ detailed ? 'expand_less' : 'expand_more' }}</i></button>
      <button class="wp-bar-btn" :class="{ on: reorder }" v-tip="'Reorder'" @click="reorder = !reorder"><i class="material-icons">format_line_spacing</i></button>
      <div class="wp-settings-wrap">
        <button class="wp-bar-btn" :class="{ on: showSettings }" v-tip="'Settings'" @click.stop="showSettings = !showSettings"><i class="material-icons">settings</i></button>
        <!-- widget-visibility checklist (real info-row settings v-menu) -->
        <div v-if="showSettings" class="wp-settings-menu" @click.stop>
          <label v-for="key in allKeys" :key="key" class="wp-settings-item">
            <span class="wp-settings-label">{{ widgetTitle(key) }}</span>
            <input type="checkbox" :checked="!hiddenWidgets[key]" @change="toggleWidgetVisible(key)" />
          </label>
        </div>
      </div>
    </div>

    <div class="wp-body">
      <!-- base-info block: identity (model) is ALWAYS shown; the status dot is always shown;
           battery + signal are gated on hasBatteryInfo / hasGsmInfo (real base-info-widget). -->
      <div v-if="!hiddenWidgets['device-status']" class="wp-baseinfo">
        <div class="wp-bi-model">
          <i class="material-icons wp-bi-ico">info</i>
          <div class="wp-bi-text"><div class="wp-bi-name">{{ modelName }}</div><div class="wp-bi-id">ID {{ deviceId }}</div></div>
        </div>
        <div class="wp-ind-row">
          <span class="wp-ind"><span class="ind-dot" :style="{ background: statusColor }"></span>{{ statusLabel }}</span>
          <span class="wp-ind" v-if="hasBatteryInfo"><i class="material-icons" :class="{ low: battery.level < 20 }">battery_full</i>{{ battery.level }}%</span>
          <span class="wp-ind" v-if="hasGsmInfo"><i class="material-icons sig">signal_cellular_4_bar</i>{{ signal }}%</span>
        </div>
      </div>

      <!-- compact widget rows (draggable) -->
      <div
        v-for="key in rowKeys"
        :key="key"
        class="wp-row"
        :class="{ dragging: dragKey === key, dropzone: overKey === key }"
        :draggable="reorder"
        @dragstart="onDragStart(key, $event)"
        @dragover.prevent="overKey = key"
        @dragleave="overKey === key && (overKey = null)"
        @drop="onDrop(key)"
        @dragend="dragKey = null; overKey = null"
      >
        <i v-if="reorder" class="material-icons wp-grip">drag_indicator</i>
        <i class="material-icons wp-row-ico">{{ meta[key].icon }}</i>
        <div class="wp-row-body">
          <!-- Location: compact = address + timestamp; detailed (chevron) expands to the real's
               full view — title, timestamp, signal, full address, coordinates, speed. -->
          <template v-if="key === 'position'">
            <template v-if="detailed">
              <div class="wp-row-title">Location</div>
              <div class="wp-row-sub">{{ relTime(state.last_update) }}</div>
              <div class="wp-row-sub wp-sig" v-if="signal != null"><i class="material-icons">signal_cellular_alt</i>Signal: {{ signal }}%</div>
              <div class="wp-row-main">{{ address || coords }}</div>
              <div class="wp-row-sub" v-if="address">{{ coords }}</div>
              <div class="wp-row-sub">Speed: {{ speedKmh }} km/h</div>
            </template>
            <template v-else>
              <div class="wp-row-main wp-clamp2">{{ address || coords }}</div>
              <div class="wp-row-sub">{{ relTime(state.last_update) }}</div>
            </template>
          </template>
          <!-- work status (On the way / Idling / …) -->
          <template v-else-if="key === 'work-status'">
            <div class="wp-row-main">{{ state.work_status || '—' }}</div>
            <div class="wp-row-sub">{{ relTime(state.last_update) }}</div>
          </template>
          <!-- odometer (own widget) — "Setup odometer" link when not configured -->
          <template v-else-if="key === 'odometer'">
            <div class="wp-row-main" v-if="counters.odometer != null">{{ counters.odometer.toLocaleString() }} km</div>
            <a v-else class="wp-setup" @click.stop="setupCounter('odometerSensorChange')">Setup odometer</a>
          </template>
          <!-- engine hours (own widget) — separate from odometer, per the real app -->
          <template v-else-if="key === 'engine-hours'">
            <div class="wp-row-main" v-if="counters.engine_hours != null">{{ counters.engine_hours.toLocaleString() }} h</div>
            <a v-else class="wp-setup" @click.stop="setupCounter('enginehoursSensorChange')">Setup engine hours</a>
          </template>
          <!-- driver -->
          <template v-else-if="key === 'driver'">
            <div class="wp-row-main">Driver is not assigned</div>
            <div class="wp-row-sub">{{ relTime(state.last_update) }}</div>
          </template>
          <!-- sensors -->
          <template v-else-if="key === 'sensors'">
            <div class="wp-row-main" v-if="sensors.length"><span v-for="(s,i) in sensors" :key="i" class="wp-sensor">{{ s.name }}: <b>{{ s.value }}{{ s.units }}</b></span></div>
            <div class="wp-row-main wp-muted" v-else>No sensors</div>
          </template>
          <!-- inputs — every input line reported by the device, not a hardcoded pair -->
          <template v-else-if="key === 'inputs'">
            <div class="wp-row-main" v-if="inputList.length"><span v-for="inp in inputList" :key="inp.key" class="wp-sensor">{{ inp.label }} <b :class="inp.on ? 'on' : 'off'">{{ inp.on ? 'ON' : 'OFF' }}</b></span></div>
            <div class="wp-row-main wp-muted" v-else>No inputs</div>
          </template>
          <!-- outputs: every output line the device reports; click asks to confirm, then
               sends tracker/output/set + shows a per-output in-flight state -->
          <template v-else-if="key === 'outputs'">
            <div v-if="!outputList.length" class="wp-row-main wp-muted">No outputs</div>
            <div v-for="o in outputList" :key="o.index" class="wp-out-line">
              <div class="wp-row-main wp-out">
                <span>{{ o.label }}</span>
                <button class="wp-toggle" :class="{ on: o.on }" :disabled="writingOut === o.index || confirmOutIdx != null" @click="askOutput(o.index)"><span class="knob"></span></button>
              </div>
              <div v-if="confirmOutIdx === o.index" class="wp-confirm">
                <span>{{ o.on ? 'Turn ' + o.label + ' OFF?' : 'Turn ' + o.label + ' ON?' }}</span>
                <button class="wp-confirm-no" @click="confirmOutIdx = null">Cancel</button>
                <button class="wp-confirm-yes" @click="toggleOutput(o)">Confirm</button>
              </div>
              <div v-else-if="writingOut === o.index" class="wp-row-sub">Sending command…</div>
            </div>
          </template>
          <!-- events -->
          <template v-else-if="key === 'events'">
            <div class="wp-row-main">Recent events</div>
            <div class="wp-row-sub" v-if="!events.length">There are no events</div>
            <div class="wp-event" v-for="(e,i) in events" :key="i"><i class="material-icons" :style="{ color: e.color }">{{ e.icon }}</i><span>{{ e.text }}</span><span class="wp-event-t">{{ e.time }}</span></div>
          </template>
        </div>
      </div>
    </div>

    <!-- in-app Street View panorama -->
    <div v-if="svOpen" class="wp-sv-modal" @click.self="closeStreetView">
      <div class="wp-sv-box">
        <div ref="svCanvas" class="wp-sv-canvas"></div>
        <button class="wp-sv-close" @click="closeStreetView"><i class="material-icons">close</i></button>
      </div>
    </div>
  </div>
</template>

<script>
import { loadGoogleMaps } from '../map/gmaps-loader.js'

// Movement palette (legacy) + the connection_status palette that drives the status dot.
const COLORS = { moving: '#2e9e4b', parked: '#f2960f', blocked: '#e53935', idle: '#9aa7b2' }
const CONN_COLORS = { active: '#69cf01', idle: '#41b81a', signal_lost: '#fe6700', offline: '#fe6700', just_registered: '#888', blocked: '#fe6700' }
const CONN_LABELS = { active: 'Active', idle: 'Idle', signal_lost: 'Signal lost', offline: 'Offline', just_registered: 'Just registered', blocked: 'Blocked' }
const MOVE_LABELS = { moving: 'Moving', parked: 'Parked', stopped: 'Stopped', lbs: 'LBS' }

// Single source of truth for the BODY detail widgets (key → icon + title). Identity (model)
// lives in the always-on indicator block, not here — matching the real base-info-widget.
// Each body widget is shown only when its data exists (widgetExist), in the smart default
// order (position first · driver/work-status next when assigned · events last).
const WIDGETS = [
  // device-status = the base-info block (status dot + battery + signal); toggled from the
  // settings checklist like the real "Device status" widget, but rendered as the fixed top block.
  { key: 'device-status', icon: 'info', title: 'Device status' },
  { key: 'position', icon: 'place', title: 'Location' },
  { key: 'work-status', icon: 'work_outline', title: 'Work status' },
  { key: 'odometer', icon: 'swap_calls', title: 'Odometer' },
  { key: 'engine-hours', icon: 'av_timer', title: 'Engine hours' },
  { key: 'driver', icon: 'person', title: 'Driver' },
  { key: 'sensors', icon: 'multiline_chart', title: 'Sensors' },
  { key: 'inputs', icon: 'exit_to_app', title: 'Inputs' },
  { key: 'outputs', icon: 'open_in_new', title: 'Outputs' },
  { key: 'events', icon: 'notifications_none', title: 'Recent events' }
]
const WIDGET_KEYS = WIDGETS.map((w) => w.key)
const WIDGET_META = WIDGETS.reduce((o, w) => { o[w.key] = { icon: w.icon }; return o }, {})
const WIDGET_TITLES = WIDGETS.reduce((o, w) => { o[w.key] = w.title; return o }, {})

export default {
  name: 'WidgetsPanel',
  props: {
    tracker: { type: Object, required: true },
    state: { type: Object, default: () => ({}) },
    // full roster (+ live states) so the header dropdown can switch the shown object.
    trackers: { type: Array, default: () => [] },
    statesById: { type: Object, default: () => ({}) },
    // when the Objects list is visible, sit to its right; when minimized, stick left.
    objectsVisible: { type: Boolean, default: true }
  },
  data() {
    return {
      address: '', geoAt: 0, streetErr: false, pickerOpen: false, svOpen: false,
      detailed: false, reorder: false, dragKey: null, overKey: null,
      showSettings: false, hiddenWidgets: {}, confirmOutIdx: null, writingOut: null,
      widgetOrder: WIDGET_KEYS.slice(),
      meta: WIDGET_META
    }
  },
  computed: {
    // Settings checklist + rendered rows both exclude widgets whose data doesn't exist, so the
    // panel never shows "Setup odometer / No sensors / No inputs" placeholders for a tracker
    // that structurally can't have them (matches the real widgetExist gate).
    allKeys() { return this.widgetOrder.filter((k) => this.widgetExist(k)) },
    visibleOrder() { return this.widgetOrder.filter((k) => !this.hiddenWidgets[k] && this.widgetExist(k)) },
    // body rows exclude device-status — it's rendered as the fixed base-info block, not a row.
    rowKeys() { return this.visibleOrder.filter((k) => k !== 'device-status') },
    // vehicle → driver widget; employee → work-status widget (the real gate is tariff/assignment).
    assetType() { return this.tracker.assetType || (/\d/.test(this.tracker.label || '') ? 'vehicle' : 'employee') },
    // Default order matching the real widget panel: device-status, location, the counters
    // (odometer before driver, as seen on the real vehicle), the assigned-kind widget, the
    // rest, events last.
    defaultOrder() {
      const order = ['device-status', 'position', 'odometer', 'engine-hours']
      if (this.assetType === 'vehicle') order.push('driver', 'work-status')
      else order.push('work-status', 'driver')
      order.push('sensors', 'inputs', 'outputs', 'events')
      return order
    },
    modelFeatures() { return this.tracker.model_features || {} },
    modelName() { return (this.tracker.source && this.tracker.source.model) || 'Unknown model' },
    deviceId() {
      const raw = String((this.tracker.source && (this.tracker.source.device_id || this.tracker.source.id)) || this.tracker.id)
      // chunk a long device id into groups of 4 (the real Br() formatter)
      return raw.length >= 7 ? raw.replace(/(.{4})/g, '$1 ').trim() : raw
    },
    // Indicator gates — battery shown iff battery_level !== null; signal shown iff a usable
    // gsm.signal_level exists (gate + value must agree, or a gsm object missing signal_level
    // would render "null%").
    hasBatteryInfo() { return this.state && this.state.battery_level != null },
    hasGsmInfo() { return !!(this.state && this.state.gsm && this.state.gsm.signal_level != null) },
    loc() { return (this.state.gps && this.state.gps.location) || {} },
    moving() { return this.state.movement_status === 'moving' },
    // Connection health (drives the dot colour) — separate from movement, per the real
    // tracker-status widget which shows BOTH: a connection_status-coloured dot + a
    // "<connection> · <movement>" subtitle.
    connStatus() {
      if (this.tracker.source && this.tracker.source.blocked) return 'blocked'
      return (this.state && this.state.connection_status) || this.tracker.connection_status || 'offline'
    },
    statusColor() { return CONN_COLORS[this.connStatus] || CONN_COLORS.offline },
    statusLabel() {
      const conn = CONN_LABELS[this.connStatus] || 'Offline'
      const mv = this.state && this.state.movement_status
      return mv && MOVE_LABELS[mv] ? conn + ' · ' + MOVE_LABELS[mv] : conn
    },
    // battery_level is a plain number in the real get_states (no `charging` field).
    battery() { const b = this.state.battery_level; return b != null ? { level: b } : null },
    // GSM (cellular) signal — real shape gsm.signal_level (not GPS satellite level).
    signal() { return this.state.gsm && this.state.gsm.signal_level != null ? this.state.gsm.signal_level : null },
    counters() { return this.state.counters || {} },
    sensors() { return this.state.sensors || [] },
    // inputs/outputs are ARRAYS of booleans in the real get_states.
    inputs() { return Array.isArray(this.state.inputs) ? this.state.inputs : [] },
    // Each input line → a labelled ON/OFF entry. Real input names come from
    // tracker/sensor/input_name/list; unnamed lines fall back to "Input N".
    inputList() {
      const NAMES = ['Ignition', 'SOS', 'Door']
      return this.inputs.map((on, i) => ({ key: i, on: !!on, label: NAMES[i] || ('Input ' + (i + 1)) }))
    },
    // Each output line → a togglable entry (was a single hardcoded "Engine block").
    outputList() {
      const arr = Array.isArray(this.state.outputs) ? this.state.outputs : []
      const NAMES = ['Engine block']
      return arr.map((on, i) => ({ index: i, on: !!on, label: (NAMES[i] || ('Output ' + (i + 1))) + ' (output ' + (i + 1) + ')' }))
    },
    coords() { return (this.loc.lat != null && this.loc.lng != null) ? this.loc.lat.toFixed(6) + ', ' + this.loc.lng.toFixed(6) : '—' },
    speedKmh() { const g = this.state.gps || {}; return g.speed != null ? Math.round(g.speed) : 0 },
    streetUrl() {
      const key = import.meta.env.VITE_GMAPS_KEY
      const l = this.loc
      if (l.lat == null || l.lng == null || !key) return ''
      return 'https://maps.googleapis.com/maps/api/streetview?size=340x150&location=' + l.lat + ',' + l.lng + '&fov=80&pitch=0&key=' + key
    },
    events() {
      const out = []
      if (this.moving) out.push({ icon: 'play_arrow', color: '#2e9e4b', text: 'Movement started', time: this.shortTime })
      if (this.battery && this.battery.level < 20) out.push({ icon: 'battery_alert', color: '#e53935', text: 'Low battery', time: this.shortTime })
      return out
    },
    shortTime() { try { return new Date(this.state.last_update).toLocaleTimeString() } catch (e) { return '' } }
  },
  watch: {
    'tracker.id': {
      immediate: true,
      handler() {
        this.address = ''
        this.streetErr = false
        this.geoAt = 0 // reset throttle so a new unit is never blocked by the previous one's timestamp
        this.showSettings = false
        this.confirmOutIdx = null
        this.loadArrangement() // per-tracker widget order + hidden set
        this.reverseGeocode()
      }
    },
    // Re-resolve the address as the SAME tracker moves (throttled by geoAt in
    // reverseGeocode; not reset here, so it refreshes at most every 4s).
    'state.last_update'() { this.reverseGeocode() }
  },
  mounted() { document.addEventListener('click', this.closeSettings) },
  beforeDestroy() {
    document.removeEventListener('click', this.closeSettings)
    // release the Street View panorama's WebGL context + Google-side listeners
    if (this._pano && this._svGmaps && this._svGmaps.event) this._svGmaps.event.clearInstanceListeners(this._pano)
    this._pano = null
  },
  methods: {
    // Open an IN-APP Google Street View panorama at the object's position (real app embeds
    // it; only falls back to an external tab if the Maps key/panorama is unavailable).
    async openStreetView() {
      const l = this.loc
      if (l.lat == null || l.lng == null) return
      this.svOpen = true
      // Generation token: the first open injects+awaits the Google Maps script (seconds on a
      // cold load). A close()+reopen while that promise is pending would leave two calls parked
      // on the SAME cached promise; without this guard both continuations build a panorama and
      // the first is orphaned (uncleaned WebGL context + listeners). Each open()/close() bumps
      // the token so a superseded continuation bails out below.
      const gen = (this._svGen = (this._svGen || 0) + 1)
      await this.$nextTick()
      try {
        const gmaps = await loadGoogleMaps()
        // A newer open() or a close() superseded this call, the panel closed, or the canvas is
        // gone — abort rather than build into a stale node or orphan a newer panorama.
        if (gen !== this._svGen || !this.svOpen || !this.$refs.svCanvas) return
        this._svGmaps = gmaps
        // Release any panorama a prior open() may have built before replacing it.
        if (this._pano && gmaps.event) gmaps.event.clearInstanceListeners(this._pano)
        const pov = { heading: (this.state.gps && this.state.gps.heading) || 0, pitch: 0 }
        // Always build a FRESH panorama: the modal is v-if, so $refs.svCanvas is a new DOM node
        // each open; reusing a cached panorama would render into the previous (detached) node.
        this._pano = new gmaps.StreetViewPanorama(this.$refs.svCanvas, { position: { lat: l.lat, lng: l.lng }, pov, zoom: 1, motionTracking: false, addressControl: true, fullscreenControl: false })
      } catch (e) {
        this.svOpen = false
        window.open('https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=' + l.lat + ',' + l.lng, '_blank')
      }
    },
    // Close the panorama and release its listeners / WebGL context.
    closeStreetView() {
      this.svOpen = false
      this._svGen = (this._svGen || 0) + 1 // supersede any open() still awaiting the Maps load
      if (this._pano && this._svGmaps && this._svGmaps.event) this._svGmaps.event.clearInstanceListeners(this._pano)
      this._pano = null
    },
    selectTracker(t) { this.pickerOpen = false; if (t.id !== this.tracker.id) this.$emit('select-tracker', t.id) },
    pickStatus(t) {
      if (t.source && t.source.blocked) return 'blocked'
      const s = this.statesById[t.id]
      if (!s || typeof s !== 'object' || !s.movement_status) return 'idle'
      return s.movement_status === 'moving' ? 'moving' : 'parked'
    },
    relTime(iso) {
      if (!iso) return '—'
      const d = new Date(iso); const s = Math.max(0, (Date.now() - d.getTime()) / 1000)
      let rel
      if (s < 60) rel = 'just now'
      else if (s < 3600) rel = Math.floor(s / 60) + ' min ago'
      else if (s < 86400) rel = Math.floor(s / 3600) + ' hours ago'
      else rel = Math.floor(s / 86400) + ' days ago'
      return rel + ' (' + d.toLocaleString() + ')'
    },
    onDragStart(key, ev) { this.dragKey = key; if (ev.dataTransfer) { ev.dataTransfer.effectAllowed = 'move'; try { ev.dataTransfer.setData('text/plain', key) } catch (e) { /* */ } } },
    onDrop(target) {
      const from = this.widgetOrder.indexOf(this.dragKey); const to = this.widgetOrder.indexOf(target)
      if (from === -1 || to === -1 || from === to) { this.overKey = null; return }
      const arr = this.widgetOrder.slice(); arr.splice(to, 0, arr.splice(from, 1)[0])
      this.widgetOrder = arr; this.dragKey = null; this.overKey = null
      this.saveArrangement()
    },
    // ---- per-tracker arrangement (order + hidden set), localStorage "widgets-{id}" ----
    closeSettings() { this.showSettings = false },
    widgetTitle(key) { return WIDGET_TITLES[key] || key },
    // Real widgetExist gate per widget: only render one whose data structurally exists.
    //  position    → a valid last location        odometer/engine-hours → model has that counter
    //  sensors     → any sensor reading            inputs/outputs        → non-empty device arrays
    //  driver      → vehicle                       work-status           → employee
    //  events      → always (empty just reads "no events", it is never hidden)
    widgetExist(key) {
      switch (key) {
        case 'position': return this.loc.lat != null
        case 'odometer': return this.modelFeatures.has_odometer !== false && this.counters.odometer != null
        case 'engine-hours': return this.modelFeatures.has_engine_hours !== false && this.counters.engine_hours != null
        case 'sensors': return this.sensors.length > 0
        case 'inputs': return this.inputs.length > 0
        case 'outputs': return (Array.isArray(this.state.outputs) ? this.state.outputs.length : 0) > 0
        case 'driver': return this.assetType === 'vehicle'
        case 'work-status': return this.assetType === 'employee'
        case 'events': return true
        default: return true
      }
    },
    loadArrangement() {
      let order = this.defaultOrder.slice(); const hidden = {}
      try {
        const saved = JSON.parse(localStorage.getItem('widgets-' + this.tracker.id) || 'null')
        if (saved && Array.isArray(saved.order)) {
          // keep only currently-known body keys (drops legacy 'model'), then splice any widget
          // missing from the saved order into its DEFAULT-ORDER position (not the end) — so a
          // key added after the user's arrangement was saved (e.g. device-status) lands where a
          // fresh install puts it, right after its preceding default-order neighbour.
          const known = saved.order.filter((k) => WIDGET_KEYS.indexOf(k) !== -1)
          let at = 0
          this.defaultOrder.forEach((k) => {
            const idx = known.indexOf(k)
            if (idx === -1) { known.splice(at, 0, k); at++ }
            else at = idx + 1
          })
          order = known
          if (saved.hidden) Object.keys(saved.hidden).forEach((k) => { if (saved.hidden[k]) hidden[k] = true })
        }
      } catch (e) { /* */ }
      this.widgetOrder = order
      this.hiddenWidgets = hidden
    },
    saveArrangement() {
      try { localStorage.setItem('widgets-' + this.tracker.id, JSON.stringify({ order: this.widgetOrder, hidden: this.hiddenWidgets })) } catch (e) { /* */ }
    },
    toggleWidgetVisible(key) {
      this.$set(this.hiddenWidgets, key, !this.hiddenWidgets[key])
      this.saveArrangement()
    },
    askOutput(idx) { this.confirmOutIdx = idx },
    async reverseGeocode() {
      const l = this.loc; if (l.lat == null) return
      const now = Date.now(); if (now - this.geoAt < 4000) return
      this.geoAt = now
      try { const j = await (await fetch('https://nominatim.openstreetmap.org/reverse?format=json&lat=' + l.lat + '&lon=' + l.lng)).json(); this.address = j.display_name || '' } catch (e) { /* */ }
    },
    async toggleOutput(o) {
      // confirmed → send the command with a per-output in-flight state
      // (real param shape: {tracker_id, output, enable}; output is 1-based).
      this.confirmOutIdx = null
      this.writingOut = o.index
      try { const { request } = await import('@/api/request'); await request('tracker/output/set', { tracker_id: this.tracker.id, output: o.index + 1, enable: !o.on }) } catch (e) { /* */ } finally { this.writingOut = null }
    },
    // "Setup odometer / engine hours" → open device configuration (real app deep-links to
    // the config sub-app; here we route to device settings, carrying which counter to set up).
    setupCounter(type) { try { this.$router.push({ path: '/devices', query: { setup: type } }) } catch (e) { /* */ } }
  }
}
</script>

<style scoped>
/* attached to the RIGHT of the Objects panel (left region, over the map) */
.widgets-panel {
  /* objects minimized: stick left but leave room for the object-list reopen button */
  position: absolute; top: 12px; left: 60px; width: var(--panel-w, 336px); max-height: calc(100% - 24px);
  background: #fff; border-radius: 8px; box-shadow: 0 2px 14px rgba(0,0,0,.22);
  display: flex; flex-direction: column; overflow: hidden; z-index: 605;
  font-family: Roboto, 'Helvetica Neue', Arial, sans-serif;
  transition: left .15s ease;
}
/* Objects list visible → sit attached to its right; minimized → stick to the left. */
.widgets-panel.shifted { left: calc(24px + var(--panel-w, 336px)); }
.wp-street { position: relative; height: 150px; background: #263238; flex: none; cursor: pointer; }
.wp-street img { width: 100%; height: 100%; object-fit: cover; display: block; }
.wp-street-fallback { width: 100%; height: 100%; background: linear-gradient(135deg, #4b6070, #263238); }
.wp-street-overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; gap: 6px; color: rgba(255,255,255,.85); font-size: 14px; pointer-events: none; text-shadow: 0 1px 3px rgba(0,0,0,.6); }
.wp-street-overlay .material-icons { font-size: 20px; }
.wp-street-title { position: absolute; top: 8px; left: 12px; color: #fff; font-size: 16px; font-weight: 600; text-shadow: 0 1px 4px rgba(0,0,0,.7); display: flex; align-items: center; gap: 2px; cursor: pointer; padding: 2px 4px; border-radius: 4px; }
.wp-street-title:hover { background: rgba(0,0,0,.25); }
.wp-street-title .material-icons { font-size: 22px; }
.wp-picker { position: absolute; top: 40px; left: 12px; z-index: 5; width: 230px; max-height: 220px; overflow-y: auto; background: #fff; border-radius: 6px; box-shadow: 0 3px 14px rgba(0,0,0,.3); padding: 4px 0; }
.wp-picker-item { display: flex; align-items: center; gap: 8px; padding: 7px 12px; font-size: 13px; color: #37474f; cursor: pointer; }
.wp-picker-item:hover { background: #f2f6f9; }
.wp-picker-item.sel { background: #e3f1f8; font-weight: 500; }
.wp-picker-dot { width: 9px; height: 9px; border-radius: 50%; flex: none; }
.wp-picker-dot.moving { background: #2e9e4b; }
.wp-picker-dot.parked { background: #f2960f; }
.wp-picker-dot.blocked { background: #e53935; }
.wp-picker-dot.idle { background: #9aa7b2; }
.wp-x { position: absolute; top: 8px; right: 8px; background: rgba(0,0,0,.35); border: none; color: #fff; border-radius: 50%; width: 28px; height: 28px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.wp-x .material-icons { font-size: 18px; }
.wp-bar { display: flex; align-items: center; gap: 4px; padding: 10px 14px; border-bottom: 1px solid #eceff1; }
.wp-bar-title { flex: 1; font-size: 16px; font-weight: 500; color: #263238; }
.wp-bar-btn { background: none; border: none; color: #90a4ae; cursor: pointer; display: flex; padding: 2px; }
.wp-bar-btn.on { color: #1e88c7; }
.wp-bar-btn .material-icons { font-size: 20px; }
.wp-settings-wrap { position: relative; display: flex; }
.wp-settings-menu { position: absolute; top: 30px; right: 0; z-index: 20; width: 210px; background: #fff; border-radius: 6px; box-shadow: 0 3px 14px rgba(0,0,0,.25); padding: 6px 0; }
/* real widget-settings menu: label on the left, checkbox on the right (no icon) */
.wp-settings-item { display: flex; align-items: center; gap: 8px; padding: 8px 14px; font-size: 13px; color: #37474f; cursor: pointer; }
.wp-settings-item:hover { background: #f2f6f9; }
.wp-settings-label { flex: 1; }
.wp-settings-item input[type="checkbox"] { flex: none; width: 16px; height: 16px; accent-color: #1e96dc; cursor: pointer; }
.wp-body { overflow-y: auto; }
.wp-baseinfo { border-bottom: 1px solid #f0f2f4; }
.wp-bi-model { display: flex; align-items: center; gap: 12px; padding: 12px 16px 6px; }
.wp-bi-ico { color: #90a4ae; font-size: 20px; flex: none; }
.wp-bi-text { min-width: 0; }
.wp-bi-name { font-size: 14px; color: #263238; }
.wp-bi-id { font-size: 12px; color: #90a4ae; margin-top: 1px; }
.wp-baseinfo .wp-ind-row { border-bottom: none; padding-top: 4px; }
.wp-ind-row { display: flex; align-items: center; gap: 18px; padding: 12px 16px; border-bottom: 1px solid #f0f2f4; font-size: 13px; color: #37474f; }
.wp-ind { display: flex; align-items: center; gap: 6px; }
.wp-ind .material-icons { font-size: 18px; color: #78909c; }
.wp-ind .material-icons.low { color: #e53935; }
.wp-ind .material-icons.sig { color: #2e9e4b; }
.ind-dot { width: 11px; height: 11px; border-radius: 50%; }
.wp-row { display: flex; gap: 12px; padding: 12px 16px; border-bottom: 1px solid #f4f6f8; align-items: flex-start; }
.wp-row.dragging { opacity: .5; }
.wp-row.dropzone { background: #eef6fb; }
.wp-grip { color: #cfd8dc; font-size: 16px; cursor: grab; margin-top: 1px; }
.wp-row-ico { color: #90a4ae; font-size: 20px; flex: none; margin-top: 1px; }
.wp-row-body { flex: 1; min-width: 0; }
.wp-row-main { font-size: 14px; color: #263238; word-break: break-word; }
.wp-row-sub { font-size: 12px; color: #90a4ae; margin-top: 2px; }
/* detailed-view (chevron) extras on the Location widget */
.wp-row-title { font-size: 14px; font-weight: 500; color: #263238; margin-bottom: 2px; }
.wp-sig { display: flex; align-items: center; gap: 4px; }
.wp-sig .material-icons { font-size: 14px; color: #2e9e4b; }
/* compact address: clamp to two lines like the real compact view */
.wp-clamp2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.wp-row-main.wp-muted { color: #b0bec5; }
.wp-setup { font-size: 14px; color: #1e88c7; cursor: pointer; }
.wp-setup:hover { text-decoration: underline; }
.wp-sensor { display: inline-block; margin-right: 12px; }
.wp-row-main b.on { color: #2e9e4b; }
.wp-row-main b.off { color: #b0bec5; }
.wp-out { display: flex; align-items: center; justify-content: space-between; }
.wp-toggle { width: 40px; height: 20px; border-radius: 10px; border: none; background: #cfd8dc; cursor: pointer; position: relative; flex: none; }
.wp-toggle.on { background: #2e9e4b; }
.wp-toggle .knob { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; transition: left .15s; }
.wp-toggle.on .knob { left: 22px; }
.wp-toggle:disabled { opacity: .6; cursor: default; }
.wp-confirm { display: flex; align-items: center; gap: 8px; margin-top: 8px; font-size: 12px; color: #546e7a; }
.wp-confirm span { flex: 1; }
.wp-confirm-no, .wp-confirm-yes { border: none; background: none; font-size: 12px; font-weight: 600; cursor: pointer; padding: 3px 6px; border-radius: 4px; }
.wp-confirm-no { color: #90a4ae; }
.wp-confirm-yes { color: #1e88c7; }
.wp-event { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #546e7a; margin-top: 4px; }
.wp-event .material-icons { font-size: 16px; }
.wp-event-t { color: #b0bec5; margin-left: auto; }
/* in-app Street View modal */
.wp-sv-modal { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; z-index: 1300; }
.wp-sv-box { position: relative; width: 80vw; max-width: 900px; height: 70vh; background: #000; border-radius: 8px; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,.5); }
.wp-sv-canvas { width: 100%; height: 100%; }
.wp-sv-close { position: absolute; top: 10px; right: 10px; z-index: 2; background: rgba(0,0,0,.5); border: none; color: #fff; border-radius: 50%; width: 34px; height: 34px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.wp-sv-close:hover { background: rgba(0,0,0,.7); }
</style>
